/**
 * The orange, pulsing "somebody wrote something on this job that you have
 * not read" mark on a job card. One component for every card that shows
 * a job — the management dashboard's slot card and calendar week card,
 * the technician board's job card — so the signal looks the same wherever
 * a reader meets it. Whether it shows is `hasUnreadNote`'s call
 * (`@vollgas/shared/hooks`), never this one's.
 *
 * <p>Shared since 2026-09-22, when the technician app got the badge too.
 * Resolves against the `status-progress` token, which both Tailwind
 * configs map.
 *
 * <p>Orange, not red (user call 2026-09-22): a note is news, not an
 * alarm, and red on this grid already means a request nobody has
 * answered. `status-progress` is the app's one orange.
 */
import type { KeyboardEvent, MouseEvent } from 'react'
import { StickyNote } from 'lucide-react'

interface Props {
  readonly label: string
  /**
   * Given, the badge is a button that opens the job's notes right there
   * (user call 2026-09-22: "clicking on the note could open a popup"). The
   * click stays on the badge — the card underneath keeps its own click.
   */
  readonly onClick?: () => void
}

export function NewNoteBadge({ label, onClick }: Props) {
  const mark = (
    <>
      <span
        aria-hidden="true"
        className="absolute inline-flex h-full w-full rounded-full bg-status-progress opacity-70 motion-safe:animate-ping"
      />
      <span className="relative inline-flex items-center rounded-full bg-status-progress px-1.5 py-0.5 text-white">
        <StickyNote size={10} strokeWidth={2.5} aria-hidden="true" />
        <span className="sr-only">{label}</span>
      </span>
    </>
  )
  if (!onClick) {
    return (
      <span className="relative inline-flex shrink-0" title={label}>
        {mark}
      </span>
    )
  }
  const open = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    e.preventDefault()
    onClick()
  }
  const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation()
      e.preventDefault()
      onClick()
    }
  }
  // A span with the button role, not a <button>: the calendar week card is
  // itself a <button>, and a button inside a button is invalid markup.
  return (
    <span
      role="button"
      tabIndex={0}
      title={label}
      aria-label={label}
      onClick={open}
      onKeyDown={onKey}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="relative inline-flex shrink-0 cursor-pointer rounded-full transition hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-status-progress"
    >
      {mark}
    </span>
  )
}
